import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { BypassLayout, Button, DesktopPagination } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, RotateCcw, Eye, Edit, Trash2 } from 'lucide-react';
import FloatingButton from '../components/common/FloatingButton';
import CreateProblemModal from '../components/ProblemAgent/CreateProblemModal';
import WorkDetail from '../components/ProblemAgent/WorkDetail';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function ProblemAgent() {
  const { work_id, page_number } = useParams();
  
  // work_id가 있으면 상세 페이지 표시
  if (work_id && work_id !== 'page') {
    return (
      <BypassLayout
        renderContent={({ isMobileView, getSafeAreaStyle }) => (
          <div 
            className={`flex flex-col ${isMobileView ? 'gap-4' : ''} ${
              isMobileView 
                ? 'h-full overflow-y-auto' 
                : 'h-[calc(100vh-2rem-50px)]'
            }`}
            style={isMobileView ? getSafeAreaStyle() : {}}
          >
            {/* 메인 카드 컨테이너 */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden rounded-3xl bg-white shadow-lg p-4">
              <WorkDetail />
            </div>
          </div>
        )}
      />
    );
  }

  // 기존 ProblemAgent 컴포넌트 내용
  return <ProblemAgentList />;
}

// 기존 ProblemAgent 내용을 별도 컴포넌트로 분리
function ProblemAgentList() {
  const { getAccessToken, handleApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { page_number } = useParams();
  const [loading, setLoading] = useState(false);
  const [workList, setWorkList] = useState([]);
  const [page, setPage] = useState(parseInt(page_number) || 1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const tableContainerRef = useRef(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // URL search params에서 필터 상태 초기화
  const searchParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState({
    work_id: searchParams.get('work_id') || '',
    work_type: searchParams.get('work_type') || '',
    work_status: searchParams.get('work_status') || ''
  });
  
  // 필터 입력 필드 ref (단축키용)
  const filterInputRefs = useRef({});

  // 새 문제 생성 모달 상태
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('prob_to_prob'); // 'prob_to_prob' or 'concept_to_prob'
  const [initialProblemId, setInitialProblemId] = useState(null);

  // location.state에서 전달받은 값 처리
  useEffect(() => {
    if (location.state?.createMode) {
      setCreateMode(location.state.createMode);
      if (location.state.selectedProblemId) {
        setInitialProblemId(location.state.selectedProblemId);
      }
      setShowCreateModal(true);
      
      // state 초기화 (뒤로가기 시 모달이 다시 열리는 것 방지) - search params는 유지
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location.state, navigate, location.pathname, location.search]);

  // 현재 필터 상태를 ref로 추적 (stale closure 방지)
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // URL search params 변경 시 필터 상태 동기화 및 데이터 재조회 (뒤로가기 시 복원)
  const prevSearchRef = useRef(location.search);
  useEffect(() => {
    // 이전 search와 현재 search가 같으면 스킵
    if (prevSearchRef.current === location.search) {
      return;
    }
    prevSearchRef.current = location.search;

    const params = new URLSearchParams(location.search);
    const urlFilters = {
      work_id: params.get('work_id') || '',
      work_type: params.get('work_type') || '',
      work_status: params.get('work_status') || ''
    };
    
    // 현재 필터와 URL 필터가 다른 경우에만 업데이트
    const currentFilters = filtersRef.current;
    if (
      urlFilters.work_id !== currentFilters.work_id ||
      urlFilters.work_type !== currentFilters.work_type ||
      urlFilters.work_status !== currentFilters.work_status
    ) {
      setFilters(urlFilters);
      // 필터 변경 시 데이터 재조회 (urlFilters를 직접 전달)
      const hasFilters = Object.values(urlFilters).some(f => f !== '');
      setPage(1);
      setHasMore(true);
      // fetchWorkList가 정의된 후에 호출하기 위해 setTimeout 사용, customFilters로 urlFilters 전달
      setTimeout(() => {
        fetchingRef.current = false; // 강제로 fetch 허용
        fetchWorkList(1, hasFilters, false, urlFilters);
      }, 0);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // fetchWithTokenRefresh 함수
  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.status === 401) {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      // 404 응답 처리 - 필터 결과가 없는 경우 빈 데이터 반환
      if (response.status === 404) {
        return {
          status: 'success',
          data: [],
          page: 1,
          total_pages: 1,
          total_items: 0,
          total_count: 0
        };
      }

      const data = await response.json();

      if (data.status_code === 401) {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      if (error.message.includes('token') || error.message.includes('unauthorized')) {
        handleApiError(error);
      }
      throw error;
    }
  }, [getAccessToken, navigate, handleApiError]);

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = useCallback(() => {
    try {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // 실제 모바일 기기인지 빠르게 체크
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileView = windowWidth < 768;
      const isDesktopResponsive = windowWidth < 768 && !isMobileDevice && windowWidth >= 375;
      
      let fixedHeight, rowHeight;
      
      if (isMobileView && isMobileDevice) {
        // 실제 모바일 기기
        // BypassLayout 상단 + 헤더 영역(필터 포함) + 카드 패딩
        // 헤더: padding 14.4px + 필터 영역 약 140px = 154px
        fixedHeight = 154 + 32; // 186px 총합 (여유 공간 제거)
        rowHeight = 180; // 각 카드의 실제 높이 (패딩 포함)
        
      } else if (isDesktopResponsive) {
        // 데스크톱 반응형 (768px 미만)
        // h-[calc(100vh-2rem-50px)] + 헤더 영역 + 테이블 헤더 + 페이지네이션
        fixedHeight = 32 + 50 + 180 + 40 + 60; // 362px (여유 공간 감소)
        rowHeight = 48;
        
      } else {
        // 데스크톱 (768px 이상)
        // h-[calc(100vh-2rem-50px)] = 82px
        // 헤더 영역: padding 16px + p-4(16px) + mb-4(16px) + 필터 높이(약 44px) = 92px
        // 테이블 헤더: 40px
        // 페이지네이션: 60px
        // 테이블 컨테이너 마진: mt-16px + px-4 pb-4 = 32px
        fixedHeight = 82 + 92 + 40 + 60 + 32; // 306px 총합
        rowHeight = 48; // 단일 행 높이
      }
      
      const availableHeight = windowHeight - fixedHeight;
      const itemsCount = Math.floor(availableHeight / rowHeight);
      
      console.log(`${isMobileView && isMobileDevice ? 'Mobile' : isDesktopResponsive ? 'Desktop-Responsive' : 'Desktop'} calculation:`, {
        windowHeight,
        windowWidth,
        fixedHeight, 
        availableHeight, 
        rowHeight,
        itemsCount,
        isMobileDevice,
        isDesktopResponsive
      });
      
      // 최소값은 1로 설정하여 항상 최소 1개는 표시
      const minItems = 1;
      const maxItems = isMobileView ? 30 : 50;
      
      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 10; // 기본값
    }
  }, []);

  // fetchWorkList 호출 추적을 위한 ref
  const fetchingRef = useRef(false);
  
  // 생성 목록 조회 (customFilters가 전달되면 해당 필터 사용, 아니면 state 필터 사용)
  const fetchWorkList = useCallback(async (pageNum = 1, useFilter = false, append = false, customFilters = null) => {
    if (loading || (append && isLoadingMore) || fetchingRef.current) return;
    
    fetchingRef.current = true;
    
    if (append) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      // 모바일 초기 로딩 최적화
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileView = window.innerWidth < 768;
      const isInitialLoad = pageNum === 1 && !append;
      
      let itemsPerPage = calculateItemsPerPage();
      
      // 모바일 초기 로딩은 5개로 제한
      if (isInitialLoad && isMobileView && isMobileDevice) {
        itemsPerPage = 5;
        console.log('Mobile initial load optimization: limiting to 5 items');
      }
      
      const params = new URLSearchParams({
        page: pageNum,
        per_page: itemsPerPage
      });

      // 모바일에서는 경량화된 데이터 요청
      if (isMobileView && isMobileDevice) {
        params.append('mobile', 'true');
        params.append('fields', 'work_id,work_type,status,user_name,user_prompt,created_at'); // 필수 필드만 요청
      }

      // customFilters가 전달되면 사용, 아니면 state의 filters 사용
      const activeFilters = customFilters || filters;
      if (useFilter) {
        if (activeFilters.work_id) params.append('work_id', activeFilters.work_id);
        if (activeFilters.work_type) params.append('work_type', activeFilters.work_type);
        if (activeFilters.work_status) params.append('work_status', activeFilters.work_status);
      }

      const response = await fetchWithTokenRefresh(`/api/probagent/queue-list?${params}`);

      if (!response) return;

      if (response.status === 'success') {
        if (append) {
          setWorkList(prev => [...prev, ...response.data]);
          // append 모드에서는 page를 요청한 pageNum으로 설정
          setPage(pageNum);
        } else {
          setWorkList(response.data);
          setPage(response.page);
        }
        setHasMore(response.page < response.total_pages);
        setTotalPages(response.total_pages || 1);
        setTotalItems(response.total_items || response.total_count || response.data.length * (response.total_pages || 1));
      }
    } catch (error) {
      console.error('생성 목록 불러오기 실패:', error);
    } finally {
      fetchingRef.current = false;
      if (append) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [filters, loading, isLoadingMore, fetchWithTokenRefresh, calculateItemsPerPage]);

  // 필터 상태를 query string으로 변환하는 헬퍼 함수
  const getFilterQueryString = useCallback((currentFilters = filters) => {
    const params = new URLSearchParams();
    if (currentFilters.work_id) params.set('work_id', currentFilters.work_id);
    if (currentFilters.work_type) params.set('work_type', currentFilters.work_type);
    if (currentFilters.work_status) params.set('work_status', currentFilters.work_status);
    return params.toString();
  }, [filters]);

  // URL 업데이트 함수 (필터 상태 포함)
  const updateURL = useCallback((pageNum, currentFilters = filters) => {
    const queryString = getFilterQueryString(currentFilters);
    const url = `/problem-agent/page/${pageNum}${queryString ? `?${queryString}` : ''}`;
    navigate(url);
  }, [filters, navigate, getFilterQueryString]);

  // 상세 페이지로 이동 (필터 상태 및 페이지 번호 유지)
  const navigateToDetail = useCallback((workId) => {
    const params = new URLSearchParams();
    // 현재 페이지 번호 추가
    params.set('page', page.toString());
    // 필터 상태 추가
    if (filters.work_id) params.set('work_id', filters.work_id);
    if (filters.work_type) params.set('work_type', filters.work_type);
    if (filters.work_status) params.set('work_status', filters.work_status);
    
    const queryString = params.toString();
    const url = `/problem-agent/${workId}${queryString ? `?${queryString}` : ''}`;
    navigate(url);
  }, [navigate, filters, page]);

  // 인피니티 스크롤 더 로드 함수
  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      const nextPage = page + 1;
      const hasFilters = Object.values(filters).some(f => f !== '');
      fetchWorkList(nextPage, hasFilters, true);
    }
  }, [hasMore, isLoadingMore, page, filters, fetchWorkList]);

  // 작업 상태 표시
  const getStatusDisplay = (status) => {
    if (status === 100 || status === 101) return { text: '대기 중', color: 'text-gray-600' };
    if (status === 150) return { text: '처리 중', color: 'text-blue-600' };
    if (status === 200) return { text: '완료', color: 'text-green-600' };
    if (status === 602) return { text: '특별 처리 대기', color: 'text-orange-600' };
    return { text: '실패', color: 'text-red-600' };
  };

  // 작업 타입 표시
  const getWorkTypeDisplay = (type) => {
    if (type === 1) return '문제→문제';
    if (type === 2) return '개념→문제';
    if (type === 3) return '문제 분석';
    return '알 수 없음';
  };

  // 필터 변경 핸들러
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // 검색 핸들러
  const handleSearch = useCallback(() => {
    setPage(1);
    setHasMore(true);
    // URL에 필터 상태 반영
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!isMobile) {
      updateURL(1, filters);
    }
    fetchWorkList(1, true, false);
  }, [filters, updateURL, fetchWorkList]);

  // 초기화 핸들러
  const handleReset = useCallback(() => {
    const emptyFilters = {
      work_id: '',
      work_type: '',
      work_status: ''
    };
    setFilters(emptyFilters);
    setPage(1);
    setHasMore(true);
    // 모바일/데스크톱에 따라 다른 경로로 navigate (필터 없이)
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    navigate(isMobile ? '/problem-agent' : '/problem-agent/page/1');
    fetchWorkList(1, false, false);
  }, [navigate, fetchWorkList]);

  // 인피니티 스크롤 핸들러 (쓰로틀링 적용)
  const scrollTimerRef = useRef(null);
  const handleWorkListScroll = useCallback((event) => {
    if (!event.target || isLoadingMore || loading) return;
    
    // 쓰로틀링: 100ms 간격으로만 실행
    if (scrollTimerRef.current) return;
    
    scrollTimerRef.current = setTimeout(() => {
      scrollTimerRef.current = null;
      
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      
      // 스크롤이 하단에서 100px 이내에 도달하면 더 많은 데이터 로드
      if (scrollHeight - scrollTop <= clientHeight + 100) {
        loadMore();
      }
    }, 100);
  }, [loadMore, isLoadingMore, loading]);

  // 컴포넌트 unmount 시 타이머 정리
  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, []);

  // 키보드 단축키 핸들러 (ESC: 초기화, Enter: 검색)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 모달이 열려있거나, 삭제 다이얼로그가 열려있으면 무시
      if (showCreateModal || isDeleteDialogOpen) return;
      
      // input 요소에서 Enter 키 처리
      if (e.key === 'Enter') {
        // 필터 입력 필드에서 Enter 키를 누르면 검색 실행
        const activeElement = document.activeElement;
        const isFilterInput = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'SELECT';
        
        if (isFilterInput) {
          e.preventDefault();
          handleSearch();
        }
      }
      
      // ESC 키: 초기화
      if (e.key === 'Escape') {
        e.preventDefault();
        handleReset();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showCreateModal, isDeleteDialogOpen, handleSearch, handleReset]);

  // 작업 삭제 핸들러
  const handleDelete = useCallback(async (workId) => {
    setIsDeleteDialogOpen(true);
    
    if (!window.confirm('작업을 삭제하시겠습니까?')) {
      // Cancel 버튼 클릭 시 플래그 유지
      setTimeout(() => setIsDeleteDialogOpen(false), 100);
      return;
    }
    
    setIsDeleteDialogOpen(false);

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${workId}`, {
        method: 'DELETE'
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('작업이 삭제되었습니다.');
        setPage(1);
        setHasMore(true);
        fetchWorkList(1, Object.values(filters).some(f => f !== ''), false);
      } else {
        alert('작업 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error deleting work:', error);
      alert('작업 삭제 중 오류가 발생했습니다.');
    }
  }, [fetchWithTokenRefresh, filters, fetchWorkList]);

  // 작업 재시도 핸들러
  const handleRetry = useCallback(async (workId) => {
    if (!window.confirm('작업을 재시도하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${workId}/retry`, {
        method: 'POST'
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('작업이 재시도 큐에 추가되었습니다.');
        setPage(1);
        setHasMore(true);
        fetchWorkList(1, Object.values(filters).some(f => f !== ''), false);
      } else {
        alert('작업 재시도에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error retrying work:', error);
      alert('작업 재시도 중 오류가 발생했습니다.');
    }
  }, [fetchWithTokenRefresh, filters, fetchWorkList]);

  // 작업 완료 처리 핸들러
  const handleComplete = useCallback(async (workId) => {
    if (!window.confirm('작업을 완료 처리하여 문제를 생성하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${workId}`, {
        method: 'POST'
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('문제가 성공적으로 생성되었습니다.');
        setPage(1);
        setHasMore(true);
        fetchWorkList(1, Object.values(filters).some(f => f !== ''), false);
      } else {
        alert('문제 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error completing work:', error);
      alert('문제 생성 중 오류가 발생했습니다.');
    }
  }, [fetchWithTokenRefresh, filters, fetchWorkList]);

  // 모달 성공 시 목록 새로고침
  const handleCreateSuccess = () => {
    setPage(1);
    setHasMore(true);
    fetchWorkList(1, false, false);
  };

  // 모바일 디바이스 체크
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // 초기 리다이렉트 처리 (데스크톱에서 /problem-agent 접근 시)
  useEffect(() => {
    if (!isMobileDevice && !page_number && location.pathname === '/problem-agent') {
      navigate('/problem-agent/page/1', { replace: true });
    }
  }, [isMobileDevice, page_number, location.pathname, navigate]);

  // 초기 데이터 로드
  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin', { replace: true });
        return;
      }
      
      // 모바일에서는 page_number 무시하고 항상 1페이지부터 시작
      const pageNum = isMobileDevice ? 1 : (parseInt(page_number) || 1);
      setPage(pageNum);
      
      // URL에서 필터 상태 확인하여 필터 적용 여부 결정
      const params = new URLSearchParams(location.search);
      const urlFilters = {
        work_id: params.get('work_id') || '',
        work_type: params.get('work_type') || '',
        work_status: params.get('work_status') || ''
      };
      const hasFilters = Object.values(urlFilters).some(f => f !== '');
      
      // URL에 필터가 있으면 해당 필터로 데이터 조회
      fetchWorkList(pageNum, hasFilters, false, hasFilters ? urlFilters : null);
    };

    checkAuthAndFetch();
  }, [page_number, isMobileDevice]); // page_number가 변경될 때마다 실행

  // 화면 크기 변경 시 per_page 재계산 (데스크톱만, 디바운싱 적용)
  useEffect(() => {
    // 모바일에서는 resize 이벤트 무시
    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    let resizeTimer;
    const handleResize = () => {
      // 모바일로 전환되면 무시
      const currentWidth = window.innerWidth;
      if (currentWidth < 768) return;
      
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // 화면 크기가 변경되면 데이터를 다시 불러옴
        if (workList.length > 0 && !loading && !isLoadingMore) {
          const hasFilters = Object.values(filters).some(f => f !== '');
          fetchWorkList(page, hasFilters);
        }
      }, 500); // 500ms 디바운싱 (더 길게)
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [page, filters, workList.length, loading, isLoadingMore]);
  



  return (
    <BypassLayout
      loading={loading && workList.length === 0}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col ${isMobileView ? 'gap-4' : ''} ${
            isMobileView 
              ? 'h-full' 
              : 'h-[calc(100vh-2rem-50px)]'
          }`}
          style={isMobileView ? getSafeAreaStyle() : {}}
        >
          {/* 네비게이션 (데스크톱만) */}
          {!isMobileView && (
            <nav className="shrink-0">
            </nav>
          )}

          {/* 메인 카드 컨테이너 */}
          <div className={`flex flex-col rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1 overflow-hidden' : 'flex-1 overflow-hidden'
          }`}>
            {/* 헤더 영역 */}
            <div className="shrink-0 border-b bg-white">
              <div style={{ 
                padding: isMobileView ? '14.4px 14.4px 0 14.4px' : '16px 16px 0 16px',
                marginBottom: isMobileView ? '0' : '0'
              }}>
                <div className={`${isMobileView ? 'pb-4' : 'p-4'} rounded-lg`}>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">생성 목록</h3>
                      <p className="text-xs text-gray-500">
                        - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>개 중 
                        <span className="font-semibold text-primary"> {workList?.length || 0}</span>개 표시 중
                      </p>
                    </div>
                    {/* 새 문제 생성 버튼 - 우측 상단으로 이동 */}
                    {!isMobileView && (
                      <Button
                        variant="primary"
                        onClick={() => setShowCreateModal(true)}
                        className="text-xs rounded-3xl"
                      >
                        새 문제 생성
                      </Button>
                    )}
                  </div>
                  
                  {/* 필터 및 액션 버튼 */}
                  <div className="space-y-4">
                    {/* 필터 입력 필드들 - 문제생성 스타일 */}
                    <div className={`${isMobileView ? 'flex gap-2' : 'flex gap-2 items-center'}`}>
                      <div className={`${isMobileView ? 'flex flex-1 flex-col gap-2' : 'flex flex-1 gap-2'}`}>
                        <input
                          type="number"
                          name="work_id"
                          placeholder="생성 ID"
                          value={filters.work_id}
                          onChange={handleFilterChange}
                          className={`${isMobileView ? 'w-full flex-1' : 'flex-1'} rounded border ${isMobileView ? 'px-2 py-1 text-xs' : 'px-3 py-2.5 text-sm'}`}
                          min="1"
                        />
                        <select
                          name="work_type"
                          value={filters.work_type}
                          onChange={handleFilterChange}
                          className={`${isMobileView ? 'w-full flex-1' : 'flex-1'} rounded border ${isMobileView ? 'px-2 py-1 text-xs' : 'px-3 py-2.5 text-sm'}`}
                        >
                          <option value="">생성 타입</option>
                          <option value="1">문제→문제</option>
                          <option value="2">개념→문제</option>
                          <option value="3">문제 분석</option>
                        </select>
                        <select
                          name="work_status"
                          value={filters.work_status}
                          onChange={handleFilterChange}
                          className={`${isMobileView ? 'w-full flex-1' : 'flex-1'} rounded border ${isMobileView ? 'px-2 py-1 text-xs' : 'px-3 py-2.5 text-sm'}`}
                        >
                          <option value="">생성 상태</option>
                          <option value="completed">완료</option>
                          <option value="processing">처리 중</option>
                          <option value="failed">실패</option>
                        </select>
                      </div>
                      <div className={`${isMobileView ? 'flex w-16 flex-col gap-2' : 'flex gap-2'}`}>
                        <Button
                          onClick={handleReset}
                          variant="secondary"
                          size="sm"
                          rounded="3xl"
                          className={`${isMobileView ? 'h-full min-h-[44px]' : 'px-6 py-2.5 text-sm'}`}
                        >
                          초기화
                        </Button>
                        <Button
                          onClick={handleSearch}
                          variant="primary"
                          size="sm"
                          rounded="3xl"
                          className={`${isMobileView ? 'h-full min-h-[44px]' : 'px-6 py-2.5 text-sm'}`}
                        >
                          조회
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 컨텐츠 영역 - 데스크톱 */}
            {!isMobileView && (
            <div className="flex-1 flex flex-col" style={{ 
              padding: '0',
              marginTop: '16px',
              overflow: 'hidden'
            }}>
              <div className="px-4 pb-4 flex-1 flex flex-col overflow-hidden">
                {loading ? (
                  <div className="py-4 text-center">로딩 중...</div>
                ) : workList.length > 0 ? (
                  <>
                    <div ref={tableContainerRef} className="flex-1 overflow-auto" style={{ minHeight: 0, width: '100%' }}>
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">생성 ID</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">생성 타입</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">생성 상태</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">작성자</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">프롬프트</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">생성일</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {workList.map((work) => {
                            const statusInfo = getStatusDisplay(work.status);
                            return (
                              <tr 
                                key={work.work_id}
                                className="group hover:bg-primary/10 cursor-pointer transition-colors duration-150"
                                onClick={() => navigateToDetail(work.work_id)}
                              >
                                <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150 group-hover:rounded-l-3xl">
                                  <span className="text-sm text-gray-900">{work.work_id}</span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{getWorkTypeDisplay(work.work_type)}</span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    statusInfo.text === '완료' ? 'bg-green-100 text-green-800' :
                                    statusInfo.text === '처리 중' ? 'bg-blue-100 text-blue-800' :
                                    statusInfo.text === '대기 중' ? 'bg-gray-100 text-gray-800' :
                                    statusInfo.text === '특별 처리 대기' ? 'bg-orange-100 text-orange-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {statusInfo.text}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{work.user_name}</span>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="text-sm text-gray-900 max-w-xs truncate" title={work.user_prompt}>
                                    {work.user_prompt}
                                  </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span className="text-sm text-gray-900">{new Date(work.created_at).toLocaleDateString()}</span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-center transition-colors duration-150 group-hover:rounded-r-3xl">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigateToDetail(work.work_id);
                                      }}
                                      className="p-1 text-blue-500 hover:text-blue-600 focus:outline-none"
                                      title="상세보기"
                                    >
                                      <Edit className="size-4" />
                                    </button>
                                    {work.status !== 150 && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(work.work_id);
                                        }}
                                        className="p-1 text-red-500 hover:text-red-600 focus:outline-none"
                                        title="삭제"
                                      >
                                        <Trash2 className="size-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* 데스크톱 페이지네이션 */}
                    {!loading && (
                      <div className="shrink-0">
                        <DesktopPagination
                          currentPage={page}
                          totalPages={totalPages}
                          onPageChange={(newPage) => {
                            updateURL(newPage);
                            fetchWorkList(newPage, Object.values(filters).some(f => f !== ''), false);
                          }}
                          itemsPerPage={calculateItemsPerPage()}
                          totalItems={totalItems}
                          showInfo={true}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-1 items-center justify-center text-sm text-gray-500 lg:text-base">
                    작업이 없습니다.
                  </div>
                )}
              </div>
            </div>
            )}

            {/* 생성 목록 - 모바일 카드 */}
            {isMobileView && (
            <div 
              className="flex-1 overflow-y-auto bg-gray-50 p-4" 
              onScroll={handleWorkListScroll}>
              <div className="space-y-3">
              {!loading && workList.length > 0 ? (
                <>
                  {workList.map((work) => {
                    const statusInfo = getStatusDisplay(work.status);
                    return (
                      <div 
                        key={work.work_id} 
                        className="relative cursor-pointer rounded-3xl border bg-white p-[14.4px] shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20 border-gray-200"
                        onClick={() => {
                          if (!isDeleteDialogOpen) {
                            navigateToDetail(work.work_id);
                          }
                        }}
                      >
                        {/* 카드 헤더 */}
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div 
                              className="text-2xl font-bold self-center"
                              style={{ 
                                color: statusInfo.text === '완료' ? '#4ABC85' : 
                                       statusInfo.text === '실패' ? '#F9344B' : '#000000'
                              }}
                            >
                              {work.work_id}
                            </div>
                            
                            <div className="flex items-center gap-2 self-center">
                              <span className="text-sm text-black font-bold">{getWorkTypeDisplay(work.work_type)}</span>
                              <span className="text-xs text-gray-600">({work.user_name})</span>
                              {work.status !== 150 && (
                                <Trash2 
                                  size={14} 
                                  className="cursor-pointer text-red-500 hover:text-red-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(work.work_id);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* 상태 표시 */}
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${
                              statusInfo.text === '완료' ? 'bg-[#4ABC85]' :
                              statusInfo.text === '처리 중' ? 'bg-blue-500' :
                              statusInfo.text === '대기 중' ? 'bg-gray-500' :
                              statusInfo.text === '특별 처리 대기' ? 'bg-orange-500' :
                              'bg-red-500'
                            }`}>
                              {statusInfo.text}
                            </span>
                          </div>
                        </div>
                        
                        {/* 프롬프트 미리보기 내용 */}
                        <div className="relative rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 mt-2">
                          <div className="space-y-1 text-xs">
                            <div><span className="font-medium">AI 지시사항:</span></div>
                            <div className="break-words">{work.user_prompt}</div>
                            <div className="text-right text-gray-500">{new Date(work.created_at).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* 로딩 더 표시 */}
                  {isLoadingMore && (
                    <div className="rounded-3xl border border-gray-200 bg-white p-4 shadow-lg transition-all duration-300">
                      <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                        <div className="size-4 animate-spin rounded-full border-b-2 border-primary"></div>
                        더 로딩 중...
                      </div>
                    </div>
                  )}

                  {/* 더 이상 데이터가 없을 때 */}
                  {!loading && !hasMore && workList.length > 0 && !isLoadingMore && (
                    <div className="text-center text-sm text-gray-500">
                      모든 작업을 불러왔습니다.
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* 로딩 중일 때 */}
                  {loading && workList.length === 0 && (
                    <div className="flex h-full items-center justify-center">
                      <div className="text-center">
                        <div className="size-8 animate-spin rounded-full border-b-2 border-primary mx-auto mb-2"></div>
                        <div className="text-sm text-gray-500">로딩 중...</div>
                      </div>
                    </div>
                  )}
                  
                  {/* 데이터가 없을 때 */}
                  {!loading && workList.length === 0 && (
                    <div className="flex h-full items-center justify-center">
                      <div className="text-center text-sm text-gray-500">
                        작업이 없습니다.
                      </div>
                    </div>
                  )}
                </>
              )}
              </div>
            </div>
            )}

          </div>

          {/* 모바일 플로팅 새 문제 생성 버튼 */}
          {isMobileView && (
            <FloatingButton
              variant="plus"
              onClick={() => setShowCreateModal(true)}
              title="새 문제 생성"
              usePortal={true}
            />
          )}

          {/* 문제 생성 모달 */}
          <CreateProblemModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleCreateSuccess}
            initialMode={createMode}
            initialProblemId={initialProblemId}
          />
        </div>
      )}
    />
  );
}

export default ProblemAgent; 